import { Auction } from '@/api';
import PBBidDetails from '@/components/ecommerce/product/ProductBid/Components/PBBidDetails';
import { AmountInput } from '@/components/general/AmountInput';
import Price from '@/components/general/Price';
import If, { Else, ElseIf } from '@/components/util/IF';
import AuctionPricePromotion from '@/contexts/AuctionPricePromotion';
import useAuth from '@/contexts/AuthContext';
import useAuction from '@/hooks/useAuction';
import useItemContext from '@/hooks/useItemContext';
import useOffers from '@/hooks/useOffers';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

const ProductEcomAuctionPromotion = ({ setCurrentDonationAmount }: { setCurrentDonationAmount: (amount: string) => void }) => {
    const { item } = useItemContext();
    const { user } = useAuth();
    const { hasAuction, auction, auctionOver, endTime } = useAuction(item, undefined, true);
    const { offerPossible } = useOffers(item, true);
    const hasAuctionFlag = hasAuction && !offerPossible;
    const [currentAmount, setCurrentAmount] = useState<string | undefined>();
    const [currentAmountTooLow, setCurrentAmountTooLow] = useState<boolean>(false);

    useEffect(() => {
        if (currentAmount) {
            if (parseFloat(currentAmount) < parseFloat(auction?.buy_now_amount || '0')) {
                setCurrentAmountTooLow(true);
            } else {
                setCurrentAmountTooLow(false);
                setCurrentDonationAmount(currentAmount);
            }
        } else {
            setCurrentAmountTooLow(false);
        }
    }, [auction?.buy_now_amount, currentAmount, setCurrentDonationAmount]);

    return (
        <If condition={hasAuctionFlag}>
            <PBBidDetails hasAuction={hasAuction} auctionOver={auctionOver} endTime={endTime} auction={auction} />
            <If condition={auction?.type === Auction.TypeEnum.BuyNow}>
                <If condition={auctionOver}>
                    <div>
                        <AuctionPricePromotion auction={auction}>{({ isPromoted, amount, promotedAmount }) => {
                            return (
                                <div>{i18next.t('common:item-text.sold-out')}
                                    <Price className={clsx('@[600px]:text-[2rem] text-[1rem] font-[700]', { 'line-through @[600px]:text-[1rem] text-red text-sm': isPromoted })} justification="L" number={amount} />
                                    <If condition={isPromoted}>
                                        <Price className={clsx('@[600px]:text-[2rem] text-[1.4rem] font-[700]')} justification="L" number={promotedAmount ?? ''} />
                                    </If>
                                </div>
                            );
                        }}
                        </AuctionPricePromotion>
                    </div>
                    <Else>
                        <div>
                            <AuctionPricePromotion auction={auction}>{({ isPromoted, amount, promotedAmount, allowDonations }) => {
                                if (allowDonations) {
                                    return (
                                        <div>{i18next.t('common:item-text.enter-donation-amount')}:
                                            <AmountInput
                                                value={currentAmount || amount}
                                                hasError={currentAmountTooLow}
                                                setValue={(value) => {
                                                    setCurrentAmount(value);
                                                }} />
                                            <motion.span
                                                animate={{ color: currentAmountTooLow ? '#D92D20' : '#000000' }}
                                                className={clsx(' text-[11px] font-[500] uppercase text-center block')}>
                                                {currentAmountTooLow ? i18next.t('common:item-text.donation-amount-too-low', { donation: amount }) : ''}
                                            </motion.span>
                                        </div>
                                    );
                                }
                                return (
                                    <div>{i18next.t('common:item-text.price')}
                                        <Price className={clsx('@[600px]:!text-[2rem] text-[1rem] font-[700]', { 'line-through @[600px]:text-[1rem] text-red text-sm': isPromoted })} justification="L" number={amount} />
                                        <If condition={isPromoted}>
                                            <Price className={clsx('@[600px]:text-[2rem] text-[1.4rem] font-[700]')} justification="L" number={promotedAmount ?? ''} />
                                        </If>
                                    </div>
                                );
                            }}
                            </AuctionPricePromotion>
                        </div>
                    </Else>
                </If>
            </If>
            <ElseIf condition={item?.current_offer_transaction}>
                <If condition={item?.owner_id !== user?.id}>
                    <div>
                        <div>{i18next.t('common:item-text.bought-for')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={item?.current_offer_transaction?.current_amount ?? '0'} /></div>
                    </div>
                    <Else>
                        <div className="flex justify-between">
                            <div>{i18next.t('common:item-text.sold-for')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={item?.current_offer_transaction?.current_amount ?? '0'} /></div>
                            <div>{i18next.t('common:offers.payout')}<Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={item?.current_offer_transaction?.payout_amount ?? '0'} /></div>
                        </div>
                    </Else>
                </If>
            </ElseIf>
        </If>
    );
};

export default ProductEcomAuctionPromotion;
